import moment from 'moment';

// Calculate checkout fees and refunds
export const calculateCheckoutImpact = (
  originalCheckOut,
  requestedCheckOut,
  policy,
  baseRate = 1000,
  checkOutPolicy = { standardTime: '10:00' }
) => {
  const checkOutStandardTime = moment(checkOutPolicy.standardTime, "HH:mm");
  const checkOutHours = checkOutStandardTime.hour();
  const checkOutMinutes = checkOutStandardTime.minute();
  const originalTime = moment(originalCheckOut).set({ hour: checkOutHours, minute: checkOutMinutes, second: 0});
  const requestedTime = moment(requestedCheckOut);
  const hoursDifference = requestedTime.diff(originalTime, 'hours', true);

  if (hoursDifference < -((policy?.earlyCheckoutRules?.minimumHoursEarly) || 0)) {
    // Early checkout
    const hoursEarly = Math.abs(hoursDifference);
    const unusedTimeValue = (hoursEarly / 24) * baseRate;
    const refundAmount = unusedTimeValue * ((policy?.earlyCheckoutRules?.refundPercentage || 0) / 100);
    const processingFee = policy?.earlyCheckoutRules?.processingFee || 0;
    const netRefund = Math.max(0, refundAmount - processingFee);

    return {
      type: 'early',
      hoursDifference: hoursEarly,
      additionalFees: -Math.floor(netRefund),
      grossRefund: unusedTimeValue,
      refundAmount: Math.floor(refundAmount),
      processingFee,
      netRefund : Math.floor(netRefund),
      canProceed: !!policy?.earlyCheckoutRules?.enabled,
      description: `Early checkout by ${hoursEarly.toFixed(1)} hours`
    };
  } else if (hoursDifference > ((policy?.lateCheckoutRules?.gracePeriodMinutes || 0) / 60)) {
    // Late checkout
    const hoursLate = hoursDifference - ((policy?.lateCheckoutRules?.gracePeriodMinutes || 0) / 60);
    let additionalFees = 0;

    if (hoursLate >= (policy?.lateCheckoutRules?.fullDayChargeAfter || Infinity)) {
      additionalFees = baseRate; // Full day charge
    } else {
      additionalFees = Math.ceil(hoursLate) * (policy?.lateCheckoutRules?.hourlyRate || 0);
    }

    return {
      type: 'late',
      // totalHoursLate: hoursDifference,
      hoursDifference: hoursDifference,
      additionalFees : Math.ceil(additionalFees),
      grossRefund: Math.ceil(additionalFees),
      processingFee: 0,
      netRefund: Math.ceil(additionalFees),
      canProceed: !!policy?.lateCheckoutRules?.enabled && hoursLate <= (policy?.lateCheckoutRules?.maximumHours || Infinity),
      description: `Late checkout by ${hoursLate.toFixed(1)} hours`
    };
  }

  return {
    type: 'onTime',
    hoursDifference: 0,
    additionalFees: 0,
    grossRefund: 0,
    processingFee: 0,
    netRefund: 0,
    canProceed: true,
    description: 'On-time checkout'
  };
};

// Check room availability for same-day booking after early checkout
export const checkSameDayAvailability = (
  unitId,
  earlyCheckoutTime,
  existingBookings
) => {
  const checkoutDate = moment(earlyCheckoutTime);
  const sameDayBookings = (existingBookings || []).filter(booking => {
    const bookingDate = moment(booking.checkIn || booking.reqFrom || booking.reqFrom);
    return bookingDate.isSame(checkoutDate, 'day') && booking.unitId === unitId;
  });

  // Calculate available hours for same-day booking
  const availableFrom = checkoutDate.clone().add(2, 'hours'); // cleaning buffer
  const nextBooking = sameDayBookings.find(booking => moment(booking.checkIn || booking.reqFrom).isAfter(availableFrom));

  const availableUntil = nextBooking ? moment(nextBooking.checkIn || nextBooking.reqFrom) : checkoutDate.clone().endOf('day');
  const availableHours = availableUntil.diff(availableFrom, 'hours', true);

  return {
    available: availableHours >= 4, // Minimum 4 hours for same-day booking
    availableFrom: availableFrom.toISOString(),
    availableUntil: availableUntil.toISOString(),
    availableHours: Math.floor(Math.max(0, availableHours))
  };
};

// Generate checkout revenue opportunities
export const calculateRevenueOpportunity = (
  earlyCheckouts,
  sameDayBookingRate = 800
) => {
  return (earlyCheckouts || []).map(checkout => {
    const availability = checkSameDayAvailability(
      checkout.unitCode,
      checkout.requestedCheckOut,
      [] // supply bookings array in real usage
    );

    if (availability.available) {
      const potentialRevenue = (availability.availableHours / 24) * sameDayBookingRate;
      const refundGiven = Math.abs(checkout.additionalFees || 0);
      const netGain = potentialRevenue - refundGiven;

      return {
        checkoutId: checkout.id || checkout._id,
        unitCode: checkout.unitCode,
        potentialRevenue,
        refundGiven,
        netGain,
        availableHours: availability.availableHours,
        recommended: netGain > 0
      };
    }

    return null;
  }).filter(Boolean);
};

// Mock checkout policy
export const defaultCheckoutPolicy = {
  id: '1',
  propertyId: '1',
  earlyCheckoutRules: {
    enabled: true,
    minimumHoursEarly: 2,
    refundPercentage: 50,
    processingFee: 50
  },
  lateCheckoutRules: {
    enabled: true,
    gracePeriodMinutes: 30,
    hourlyRate: 100,
    maximumHours: 6,
    fullDayChargeAfter: 4
  },
  isActive: true
};

